import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useSeason } from "../context/SeasonContext";
import StatAdjustmentModal from "../components/captain/StatAdjustmentModal";
import DashboardHero from "../components/captain/DashboardHero";
import PerformanceMetrics from "../components/captain/PerformanceMetrics";
import PaymentTracker from "../components/captain/PaymentTracker";
import AnnouncementBoard from "../components/captain/AnnouncementBoard";
import GameDayChecklist from "../components/captain/GameDayChecklist";
import LineupBuilder from "../components/captain/LineupBuilder";
import { motion } from "motion/react";
import { Calendar, Users, Bell, Printer, Mail, Download } from "lucide-react";
import { parseLocalDate } from "../utils/date";

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-500/10 dark:bg-orange-500/20">
      <Icon size={22} className="text-orange-600 dark:text-orange-400" />
    </div>
    <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
      {title}
    </h2>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending:
      "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
    approved:
      "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    rejected: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300",
  };
  const icons = { pending: "⏳", approved: "✓", rejected: "✗" };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${map[status] || ""}`}
    >
      {icons[status]} {status}
    </span>
  );
};

const TeamCaptainDashboard = () => {
  const { user } = useAuth();
  const { activeSeason, registrationSeason } = useSeason();

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [activeTab, setActiveTab] = useState("results");

  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [finishedGames, setFinishedGames] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [adjustmentRequests, setAdjustmentRequests] = useState([]);

  const [loading, setLoading] = useState(true);
  const [gamesLoading, setGamesLoading] = useState(false);
  //const [uploading, setUploading] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);

  // Load captain's teams
  const loadAllTeams = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from("teams")
      .select(
        `
        *,
        season:seasons!teams_season_id_fkey(name, status)
      `,
      )
      .eq("captain_id", user.id)
      .order("created_at", { ascending: false });

    setTeams(data || []);

    if (data?.length) {
      const preferred =
        data.find((t) => t.season_id === activeSeason?.id) ||
        data.find((t) => t.season_id === registrationSeason?.id) ||
        data[0];
      setSelectedTeamId(preferred.id);
    }
    setLoading(false);
  }, [user?.id, activeSeason, registrationSeason]);

  useEffect(() => {
    loadAllTeams();
  }, [loadAllTeams]);

  const loadTeamData = useCallback(async (teamId) => {
    if (!teamId) return;

    // 1. Fetch Team Details
    const { data: teamData, error: teamError } = await supabase
      .from("teams")
      .select("*")
      .eq("id", teamId)
      .single();

    if (teamError) {
      console.error("Error loading team:", teamError);
      return;
    }

    if (teamData) {
      setTeam(teamData);

      // 2. Fetch Players AND their related Game Stats in one join query
      const { data: playersData, error: playersError } = await supabase
        .from("players")
        .select(
          `
        *,
        game_stats (
          points,
          rebounds,
          assists
        )
      `,
        )
        .eq("team_id", teamData.id);

      if (playersError) {
        console.error("Error loading players:", playersError);
        return;
      }

      if (playersData) {
        // 3. Process the nested game_stats arrays into aggregate values
        const processedPlayers = playersData.map((player) => {
          const allStats = player.game_stats || [];

          // ── DNP Filter: Exclude games where all stats are zero ──
          const stats = allStats.filter((row) => {
            const isDNP =
              row.points === 0 && row.rebounds === 0 && row.assists === 0;
            return !isDNP;
          });

          // Sum up the points and rebounds across all games for this player
          const totalPoints = stats.reduce(
            (sum, row) => sum + (row.points || 0),
            0,
          );
          const totalRebounds = stats.reduce(
            (sum, row) => sum + (row.rebounds || 0),
            0,
          );

          // Calculate per-game averages safely to prevent division by zero
          const gamesPlayed = stats.length;
          const ppg =
            gamesPlayed > 0 ? (totalPoints / gamesPlayed).toFixed(1) : "0.0";
          const rpg =
            gamesPlayed > 0 ? (totalRebounds / gamesPlayed).toFixed(1) : "0.0";

          return {
            ...player,
            totalPoints,
            totalRebounds,
            pointsPerGame: parseFloat(ppg),
            reboundsPerGame: parseFloat(rpg),
            gamesPlayed,
          };
        });

        setPlayers(processedPlayers);
      }
    }
  }, []);

  // Load games
  const loadGames = useCallback(async (teamId) => {
    if (!teamId) return;
    setGamesLoading(true);

    const [{ data: finished }, { data: upcoming }] = await Promise.all([
      supabase
        .from("games")
        .select(
          `
          id, game_date, team1_score, team2_score, game_type, status,
          team1:teams!games_team1_id_fkey(id, name, logo_url),
          team2:teams!games_team2_id_fkey(id, name, logo_url)
        `,
        )
        .eq("status", "finished")
        .or(`team1_id.eq.${teamId},team2_id.eq.${teamId}`)
        .order("game_date", { ascending: false })
        .limit(10),

      supabase
        .from("games")
        .select(
          `
          id, game_date, court,
          team1:teams!games_team1_id_fkey(id, name, logo_url),
          team2:teams!games_team2_id_fkey(id, name, logo_url)
        `,
        )
        .eq("status", "scheduled")
        .or(`team1_id.eq.${teamId},team2_id.eq.${teamId}`)
        .order("game_date", { ascending: true })
        .limit(6),
    ]);

    setFinishedGames(finished || []);
    setUpcomingGames(upcoming || []);
    setGamesLoading(false);
  }, []);

  const loadAdjustmentRequests = useCallback(async (teamId) => {
    const { data } = await supabase
      .from("stat_adjustment_requests")
      .select("*")
      .eq("team_id", teamId)
      .order("created_at", { ascending: false });
    setAdjustmentRequests(data || []);
  }, []);

  useEffect(() => {
    if (selectedTeamId) {
      loadTeamData(selectedTeamId);
      loadGames(selectedTeamId);
      loadAdjustmentRequests(selectedTeamId);
    }
  }, [selectedTeamId, loadTeamData, loadGames, loadAdjustmentRequests]);

  // Logo Upload
  /*
  const handleTeamLogoUpload = async (file) => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const fileName = `team-${team.id}-${Date.now()}.${ext}`; 
    const { error: uploadError } = await supabase.storage
      .from("team-logos")
      .upload(fileName, file);
    if (!uploadError) {
      const {
        data: { publicUrl },
      } = supabase.storage.from("team-logos").getPublicUrl(fileName);
      await supabase
        .from("teams")
        .update({ logo_url: publicUrl })
        .eq("id", team.id);
      setTeam((prev) => ({ ...prev, logo_url: publicUrl }));
    }
    setUploading(false);
  };*/

  // Waiver Upload
  const handleWaiverUpload = async (playerId, file) => {
    const ext = file.name.split(".").pop();
    const fileName = `waiver-${playerId}-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from("liability-waivers")
      .upload(fileName, file);
    if (!uploadError) {
      const {
        data: { publicUrl },
      } = supabase.storage.from("liability-waivers").getPublicUrl(fileName);
      await supabase
        .from("players")
        .update({
          liability_waiver_url: publicUrl,
          waiver_signed_date: new Date().toISOString().split("T")[0],
        })
        .eq("id", playerId);
      loadTeamData(selectedTeamId);
    }
  };

  const updatePlayerInfo = async (playerId, field, value) => {
    const { error } = await supabase
      .from("players")
      .update({ [field]: value })
      .eq("id", playerId);
    if (!error) {
      setPlayers((prev) =>
        prev.map((p) => (p.id === playerId ? { ...p, [field]: value } : p)),
      );
    }
  };

  // Enhanced Quick Actions
  const quickActions = [
    {
      label: "Email Team",
      icon: Mail,
      onClick: () => {
        const emails = players
          .filter((p) => p.email)
          .map((p) => p.email)
          .join(",");
        if (emails) window.location.href = `mailto:${emails}`;
        else alert("No player emails available");
      },
      color: "blue",
    },
    {
      label: "Waiver Reminders",
      icon: Bell,
      onClick: () => {
        const pending = players.filter((p) => !p.liability_waiver_url);
        if (pending.length === 0) {
          alert("All waivers are signed! 🎉");
        } else {
          alert(
            `${pending.length} player(s) need to sign waivers. Email reminders coming soon!`,
          );
        }
      },
      color: "amber",
    },
    {
      label: "Print Score Sheet",
      icon: Printer,
      onClick: () => alert("Print functionality coming soon"),
      color: "purple",
    },
    {
      label: "Export Roster",
      icon: Download,
      onClick: () => {
        const csv = [
          ["Name", "Jersey #", "Email", "Phone", "Waiver Status"].join(","),
          ...players.map((p) =>
            [
              p.name,
              p.jersey_number || "",
              p.email || "",
              p.phone || "",
              p.liability_waiver_url ? "Signed" : "Pending",
            ].join(","),
          ),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${team?.name || "team"}-roster.csv`;
        a.click();
      },
      color: "emerald",
    },
  ];

  if (loading)
    return <div className="text-center py-12">Loading your teams...</div>;
  if (!teams.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-md p-10 text-center">
        <h1 className="text-3xl font-bold mb-3">No Team Found</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Contact the admin if you believe this is an error.
        </p>
      </div>
    );
  }

  const currentTeam = teams.find((t) => t.id === selectedTeamId);

  return (
    <div className="space-y-8">
      {/* Team Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow p-6">
        <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
          Your Teams
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {teams.map((t) => (
            <motion.button
              key={t.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedTeamId(t.id)}
              className={`p-4 rounded-2xl border transition-all text-left ${
                selectedTeamId === t.id
                  ? "border-orange-600 bg-orange-50 dark:bg-orange-950/30"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                {t.logo_url ? (
                  <img
                    src={t.logo_url}
                    alt=""
                    className="w-12 h-12 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center text-3xl">
                    🏀
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {t.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t.season?.name || "Unknown Season"}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Dashboard Hero - New Modern Component */}
      {currentTeam && (
        <DashboardHero
          team={currentTeam}
          upcomingGames={upcomingGames}
          finishedGames={finishedGames}
        />
      )}

      {/* Performance Metrics - New Component */}
      {currentTeam && (
        <PerformanceMetrics
          players={players}
          finishedGames={finishedGames}
          team={currentTeam}
        />
      )}

      {/* Quick Actions - Enhanced */}
      {activeTab === "team" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map((action, i) => {
            const colorClasses = {
              blue: "from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/30 border-blue-200 dark:border-blue-800 hover:border-blue-400",
              amber:
                "from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/30 border-amber-200 dark:border-amber-800 hover:border-amber-400",
              purple:
                "from-purple-50 to-violet-50 dark:from-purple-950/40 dark:to-violet-950/30 border-purple-200 dark:border-purple-800 hover:border-purple-400",
              emerald:
                "from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400",
            };
            const colors = colorClasses[action.color] || colorClasses.blue;

            return (
              <motion.button
                key={i}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={action.onClick}
                className={`flex flex-col items-center gap-2 bg-gradient-to-br ${colors}
                           border-2 px-4 py-4 rounded-2xl transition-all shadow-sm hover:shadow-md`}
              >
                <action.icon
                  size={24}
                  className="text-gray-700 dark:text-gray-300"
                />
                <span className="font-semibold text-sm text-gray-900 dark:text-white text-center">
                  {action.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Tabs - Enhanced Segmented Control */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700">
        <div className="border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-2">
          <div className="flex overflow-x-auto gap-1 scrollbar-hide snap-x snap-mandatory">
            {[
              { key: "results", label: "Game Results" },
              { key: "team", label: "My Team" },
              { key: "payments", label: "Payments" },
              { key: "announcements", label: "Announcements" },
              { key: "lineup", label: "Lineup" },
            ].map((tab) => (
              <motion.button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`snap-start shrink-0 py-3 px-4 text-sm font-semibold rounded-2xl transition-all whitespace-nowrap ${
                  activeTab === tab.key
                    ? "bg-orange-600 text-white shadow-md"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                {tab.label}
              </motion.button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* === MY TEAM TAB === */}
          {activeTab === "team" && currentTeam && (
            <div className="space-y-10">
              {/* Waiver Summary */}
              <div>
                <SectionHeader icon={Users} title="Waiver Status" />
                <div className="grid grid-cols-3 gap-4">
                  {[
                    {
                      label: "Signed",
                      count: players.filter((p) => p.liability_waiver_url)
                        .length,
                      color: "green",
                    },
                    {
                      label: "Pending",
                      count: players.filter((p) => !p.liability_waiver_url)
                        .length,
                      color: "red",
                    },
                    { label: "Total", count: players.length, color: "blue" },
                  ].map(({ label, count, color }) => (
                    <div
                      key={label}
                      className={`bg-${color}-50 dark:bg-${color}-900/30 border border-${color}-200 dark:border-${color}-700 rounded-2xl p-5 text-center`}
                    >
                      <div
                        className={`text-4xl font-bold text-${color}-600 dark:text-${color}-400`}
                      >
                        {count}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Waivers {label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Roster */}
              <div>
                <SectionHeader icon={Users} title="Team Roster" />
                {/* Your existing roster code here */}
                {players.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No players on roster yet.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {players.map((player, index) => (
                      <motion.div
                        key={player.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.01, y: -2 }}
                        className="border-2 dark:border-gray-600 rounded-2xl p-5 bg-white dark:bg-gray-800
                                   hover:border-orange-300 dark:hover:border-orange-700 transition-all shadow-sm hover:shadow-md"
                        style={{
                          borderLeftWidth: "4px",
                          borderLeftColor: player.liability_waiver_url
                            ? "#10b981"
                            : "#f59e0b",
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                              {player.name}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-sm">
                              <label className="flex items-center gap-2">
                                <span className="text-gray-500 dark:text-gray-400 shrink-0">
                                  Jersey #
                                </span>
                                <input
                                  type="number"
                                  value={player.jersey_number || ""}
                                  onChange={(e) =>
                                    updatePlayerInfo(
                                      player.id,
                                      "jersey_number",
                                      e.target.value,
                                    )
                                  }
                                  className="w-16 px-2 py-1 border dark:border-gray-500 rounded-lg
                                                                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                              </label>
                              <label className="flex items-center gap-2">
                                <span className="text-gray-500 dark:text-gray-400 shrink-0">
                                  Email
                                </span>
                                <input
                                  type="email"
                                  value={player.email || ""}
                                  onChange={(e) =>
                                    updatePlayerInfo(
                                      player.id,
                                      "email",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="player@email.com"
                                  className="flex-1 min-w-0 px-2 py-1 border dark:border-gray-500 rounded-lg
                                                                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                                />
                              </label>
                              <label className="flex items-center gap-2">
                                <span className="text-gray-500 dark:text-gray-400 shrink-0">
                                  Phone
                                </span>
                                <input
                                  type="tel"
                                  value={player.phone || ""}
                                  onChange={(e) =>
                                    updatePlayerInfo(
                                      player.id,
                                      "phone",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="(216) 555-0123"
                                  className="flex-1 min-w-0 px-2 py-1 border dark:border-gray-500 rounded-lg
                                                                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                                />
                              </label>
                            </div>
                          </div>

                          <div className="shrink-0 text-center">
                            {player.liability_waiver_url ? (
                              <>
                                <div className="text-green-600 dark:text-green-400 font-semibold text-sm">
                                  ✓ Signed
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {player.waiver_signed_date}
                                </p>
                                <a
                                  href={player.liability_waiver_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 text-xs hover:underline"
                                >
                                  View
                                </a>
                              </>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  id={`waiver-${player.id}`}
                                  className="hidden"
                                  onChange={(e) =>
                                    e.target.files[0] &&
                                    handleWaiverUpload(
                                      player.id,
                                      e.target.files[0],
                                    )
                                  }
                                />
                                <label
                                  htmlFor={`waiver-${player.id}`}
                                  className="inline-block bg-red-600 text-white px-3 py-2 rounded-lg cursor-pointer
                                                                                   hover:bg-red-700 text-xs font-medium transition-colors"
                                >
                                  Upload Waiver
                                </label>
                                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                  Required
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === PAYMENTS TAB === */}
          {activeTab === "payments" && currentTeam && (
            <PaymentTracker
              team={currentTeam}
              players={players}
              season={{ id: currentTeam.season_id, ...currentTeam.season }}
            />
          )}

          {/* === ANNOUNCEMENTS TAB === */}
          {activeTab === "announcements" && currentTeam && (
            <AnnouncementBoard team={currentTeam} />
          )}

          {/* === LINEUP TAB === */}
          {activeTab === "lineup" && currentTeam && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <LineupBuilder team={currentTeam} players={players} />
            </motion.div>
          )}

          {/* === GAME RESULTS TAB === */}
          {activeTab === "results" && (
            <div className="space-y-10">
              {/* Game Day Checklist */}
              {upcomingGames.length > 0 && (
                <GameDayChecklist
                  game={upcomingGames[0]}
                  players={players}
                  team={currentTeam}
                />
              )}

              {/* Upcoming Games */}
              <div>
                <SectionHeader icon={Calendar} title="Upcoming Games" />
                {gamesLoading ? (
                  <p className="text-center py-8">Loading schedule...</p>
                ) : upcomingGames.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No upcoming games scheduled.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {upcomingGames.map((game, index) => (
                      <motion.div
                        key={game.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.01, x: 4 }}
                        className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-sky-50
                                   dark:from-blue-950/40 dark:to-sky-950/30 border-2 border-blue-200
                                   dark:border-blue-800 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all"
                      >
                        <div className="flex-1">
                          <p className="font-bold text-lg text-gray-900 dark:text-white">
                            {game.team1?.name} vs {game.team2?.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {parseLocalDate(game.game_date).toLocaleDateString(
                              "en-US",
                              {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                              },
                            )}{" "}
                            • {game.court || "TBD"}
                          </p>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            alert("Printing score sheet...");
                          }}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white
                                     px-5 py-2.5 rounded-xl transition-colors shadow-sm"
                        >
                          <Printer size={18} /> Print
                        </motion.button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Finished Games */}
              <div>
                <SectionHeader icon={Calendar} title="Game Results" />
                {gamesLoading ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-12">
                    Loading games…
                  </p>
                ) : finishedGames.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-12">
                    No finished games yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {finishedGames.map((game, index) => {
                      const isHome = game.team1?.id === team.id;
                      const myScore = isHome
                        ? game.team1_score
                        : game.team2_score;
                      const oppScore = isHome
                        ? game.team2_score
                        : game.team1_score;
                      const opponent = isHome ? game.team2 : game.team1;
                      const won = myScore > oppScore;
                      const tied = myScore === oppScore;
                      const request = adjustmentRequests.find(
                        (r) => r.game_id === game.id,
                      );
                      const localDate = parseLocalDate(game.game_date);

                      return (
                        <motion.button
                          key={game.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ scale: 1.01, x: 4 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => setSelectedGame(game)}
                          className="w-full text-left border-2 dark:border-gray-700 rounded-2xl p-4
                                     bg-white dark:bg-gray-800 hover:border-orange-300 dark:hover:border-orange-700
                                     transition-all shadow-sm hover:shadow-md group"
                        >
                          <div className="flex items-center gap-4">
                            {/* W/L pill */}
                            <div
                              className={`shrink-0 w-10 h-10 rounded-xl flex items-center 
                                justify-center font-black text-sm ${
                                  won
                                    ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400"
                                    : tied
                                      ? "bg-gray-100 dark:bg-gray-700 text-gray-500"
                                      : "bg-red-100   dark:bg-red-900/40   text-red-500   dark:text-red-400"
                                }`}
                            >
                              {won ? "W" : tied ? "T" : "L"}
                            </div>

                            {/* Game info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-gray-900 dark:text-white">
                                  {myScore} – {oppScore}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 text-sm truncate">
                                  vs {opponent?.name}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                {localDate.toLocaleDateString("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })}
                                {game.game_type === "scrimmage" && (
                                  <span className="ml-2 text-gray-400 italic">
                                    scrimmage
                                  </span>
                                )}
                              </p>
                            </div>

                            {/* Request status or CTA */}
                            <div className="shrink-0 flex items-center gap-2">
                              {request ? (
                                <StatusBadge status={request.status} />
                              ) : (
                                <span className="text-xs font-semibold text-orange-500 dark:text-orange-400 group-hover:text-orange-600 transition-colors">
                                  Request adjustment →
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Admin notes on resolved requests */}
                          {request &&
                            request.status !== "pending" &&
                            request.admin_notes && (
                              <div className="mt-3 pt-3 border-t dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-semibold text-gray-600 dark:text-gray-300">
                                  Admin note:{" "}
                                </span>
                                {request.admin_notes}
                              </div>
                            )}
                        </motion.button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {selectedGame && (
        <StatAdjustmentModal
          game={selectedGame}
          teamId={selectedTeamId}
          onClose={() => setSelectedGame(null)}
          onSubmitted={() => {
            loadGames(selectedTeamId);
            loadAdjustmentRequests(selectedTeamId);
          }}
        />
      )}
    </div>
  );
};

export default TeamCaptainDashboard;
